import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { MatSnackBar } from '@angular/material/snack-bar';
import { formHelper } from '@helpers';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { regexHelper } from '@helpers';
import { ConfirmModalService } from '../../../core/services/confirm-modal.service';
import {
  ContactsService,
  NotificationsService,
  CollectionsService,
  SavedsearchesService,
  ContactsInterface,
  UsersService,
  UserDataInterface,
  UserInterface,
  AccountNotificationsInterface,
  NotificationTypeEnum,
  generalHelpers,
  MediaService,
  ProfilePhotoService,
} from '@mzima-client/sdk';
import { DeleteContactModalComponent } from './delete-contact-modal/delete-contact-modal.component';

enum AccountTypeEnum {
  Email = 'email',
  Phone = 'phone',
}

interface AccountTypeInterface {
  name: string;
  value: AccountTypeEnum;
}

@Component({
  selector: 'app-account-settings-modal',
  templateUrl: './account-settings-modal.component.html',
  styleUrls: ['./account-settings-modal.component.scss'],
})
export class AccountSettingsModalComponent implements OnInit {
  @ViewChild('updatePasswordFields') public updatePasswordFields: ElementRef;
  public profile: UserInterface;
  public contacts: ContactsInterface[];
  public isUpdatingPassword = false;
  public selectedTabIndex = 0;
  public isLoading: boolean;
  public isContactsChanged: boolean;
  public isAddAccountFormOpen: boolean;
  public matcher = new formHelper.FormErrorStateMatcher();
  public accountTypes: AccountTypeInterface[] = [
    {
      name: 'Email',
      value: AccountTypeEnum.Email,
    },
    {
      name: 'Phone',
      value: AccountTypeEnum.Phone,
    },
  ];
  public profileForm: FormGroup;
  public addAccountForm: FormGroup;
  public notifications: AccountNotificationsInterface[] = [];
  NotificationTypeEnum = NotificationTypeEnum;
  errorMap: { [key: string]: string | null } = {};
  public mapStyles = [
    { value: 'streets', label: 'user.map_style.streets' },
    { value: 'satellite', label: 'user.map_style.satellite' },
    { value: 'satelliteStreets', label: 'user.map_style.satelliteStreets' },
    { value: 'hOSM', label: 'user.map_style.hosm' },
    { value: 'outdoors', label: 'user.map_style.outdoors' },
    { value: 'light', label: 'user.map_style.light' },
    { value: 'dark', label: 'user.map_style.dark' },
    { value: 'cartoPositron', label: 'user.map_style.cartoPositron' },
    { value: 'cartoDark', label: 'user.map_style.cartoDark' },
    { value: 'cartoVoyager', label: 'user.map_style.cartoVoyager' },
    { value: 'esriImagery', label: 'user.map_style.esriImagery' },
    { value: 'openTopo', label: 'user.map_style.openTopo' },
  ];
  private mapSettingId: number | null = null;
  public maxMarkersOptions = [
    { value: '0', label: 'user.max_markers.unlimited' },
    { value: '500', label: 'user.max_markers.500' },
    { value: '1000', label: 'user.max_markers.1000' },
    { value: '2000', label: 'user.max_markers.2000' },
    { value: '5000', label: 'user.max_markers.5000' },
    { value: '10000', label: 'user.max_markers.10000' },
  ];
  private maxMarkersSettingId: number | null = null;
  public userPhoto: string = '//www.gravatar.com/avatar/00000000000000000000000000000000?d=retro&s=128';
  public photoSettingId: number | null = null;
  public isUploadingPhoto = false;

  private checkPasswords: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
    if (!group) return null;
    const pass = group.get('password')?.value;
    const confirmPass = group.get('confirmPassword')?.value;
    if (confirmPass) {
      return pass === confirmPass ? null : { notSame: true };
    }
    return null;
  };

  constructor(
    private usersService: UsersService,
    private contactsService: ContactsService,
    private formBuilder: FormBuilder,
    private confirmModalService: ConfirmModalService,
    private notificationsService: NotificationsService,
    private collectionsService: CollectionsService,
    private translate: TranslateService,
    private matDialogRef: MatDialogRef<AccountSettingsModalComponent>,
    private dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private savedsearchesService: SavedsearchesService,
    private mediaService: MediaService,
    private profilePhotoService: ProfilePhotoService,
  ) {
    this.profileForm = this.formBuilder.group(
      {
        role: [''],
        display_name: ['', [Validators.required]],
        email: ['', [Validators.required, Validators.pattern(regexHelper.emailValidate())]],
        password: [''],
        confirmPassword: [''],
        map_style: [''],
        max_markers: ['0'],
      },
      { validators: this.checkPasswords },
    );

    this.initAccountForm();
  }

  private initAccountForm() {
    this.addAccountForm = this.formBuilder.group({
      type: ['email', [Validators.required]],
      name: ['', [Validators.required, Validators.pattern(regexHelper.emailValidate())]],
    });
  }

  ngOnInit(): void {
    this.getTabInformation();
  }

  private getProfile(): void {
    this.usersService.getCurrentUser().subscribe({
      next: (response) => {
        const { result } = response;
        this.profile = result;
        this.profileForm.patchValue({
          role: this.profile.role,
          display_name: this.profile.realname,
          email: this.profile.email,
        });

        this.profileForm.markAsPristine();
        this.isLoading = false;
        this.loadUserMapStyle();
      },
    });
  }

  private loadUserMapStyle() {
    if (!this.profile?.id) return;
    this.userPhoto = `//www.gravatar.com/avatar/${
      this.profile.gravatar || '00000000000000000000000000000000'
    }?d=retro&s=128`;
    this.usersService.getUserSettings(this.profile.id).subscribe({
      next: (response) => {
        const mapStyleSetting = response.results?.find(
          (s: any) => s.config_key === 'map_style',
        );
        if (mapStyleSetting) {
          this.profileForm.patchValue({ map_style: mapStyleSetting.config_value });
          this.mapSettingId = mapStyleSetting.id;
        }
        const maxMarkersSetting = response.results?.find(
          (s: any) => s.config_key === 'max_markers',
        );
        if (maxMarkersSetting) {
          this.profileForm.patchValue({ max_markers: String(maxMarkersSetting.config_value) });
          this.maxMarkersSettingId = maxMarkersSetting.id;
        }
        const photoSetting = response.results?.find(
          (s: any) => s.config_key === 'profile_photo',
        );
        if (photoSetting) {
          this.photoSettingId = photoSetting.id;
          if (photoSetting.config_value?.photo_url) {
            this.userPhoto = photoSetting.config_value.photo_url;
          }
        }
      },
      error: (err) => console.log(err),
    });
  }

  public onPhotoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    if (!file.type.startsWith('image/')) {
      this._snackBar.open(this.translate.instant('user.avatar.invalid_type'), this.translate.instant('message.button.default'));
      return;
    }
    this.uploadPhoto(file);
  }

  private uploadPhoto(file: File): void {
    this.isUploadingPhoto = true;
    this.mediaService.uploadFile(file, 'profilePhoto').subscribe({
      next: (response: any) => {
        const mediaId = response?.result?.id;
        let photoUrl = response?.result?.original_file_url;
        if (photoUrl && photoUrl[0] === '/') {
          photoUrl = window.location.origin + photoUrl;
        }
        if (mediaId && photoUrl) {
          this.saveUserProfilePhoto(mediaId, photoUrl);
        } else {
          this.isUploadingPhoto = false;
          this._snackBar.open(this.translate.instant('user.avatar.upload_failed'), this.translate.instant('message.button.default'));
        }
      },
      error: () => {
        this.isUploadingPhoto = false;
        this._snackBar.open(this.translate.instant('user.avatar.upload_failed'), this.translate.instant('message.button.default'));
      },
    });
  }

  private saveUserProfilePhoto(mediaId: number, photoUrl: string): void {
    if (!this.profile?.id) return;
    const configValue = { media_id: mediaId, photo_url: photoUrl };
    if (this.photoSettingId) {
      this.usersService
        .updateUserSettings(String(this.profile.id), { config_value: configValue }, this.photoSettingId)
        .subscribe({
          next: () => {
            this.profilePhotoService.clearCache(String(this.profile.id));
            this.userPhoto = photoUrl;
            this.isUploadingPhoto = false;
            this._snackBar.open(this.translate.instant('user.avatar.updated'), this.translate.instant('message.button.default'));
          },
          error: () => {
            this.isUploadingPhoto = false;
            this._snackBar.open(this.translate.instant('user.avatar.upload_failed'), this.translate.instant('message.button.default'));
          },
        });
    } else {
      this.usersService
        .postUserSettings(String(this.profile.id), {
          config_key: 'profile_photo',
          config_value: configValue,
        })
        .subscribe({
          next: (response: any) => {
            this.profilePhotoService.clearCache(String(this.profile.id));
            this.photoSettingId = response?.result?.id || null;
            this.userPhoto = photoUrl;
            this.isUploadingPhoto = false;
            this._snackBar.open(this.translate.instant('user.avatar.updated'), this.translate.instant('message.button.default'));
          },
          error: () => {
            this.isUploadingPhoto = false;
            this._snackBar.open(this.translate.instant('user.avatar.upload_failed'), this.translate.instant('message.button.default'));
          },
        });
    }
  }

  public removePhoto(): void {
    if (!this.profile?.id || !this.photoSettingId) return;
    this.usersService.deleteUserSetting(String(this.profile.id), this.photoSettingId).subscribe({
      next: () => {
        this.profilePhotoService.clearCache(String(this.profile.id));
        this.photoSettingId = null;
        this.userPhoto = `//www.gravatar.com/avatar/${
          this.profile.gravatar || '00000000000000000000000000000000'
        }?d=retro&s=128`;
        this._snackBar.open(this.translate.instant('user.avatar.removed'), this.translate.instant('message.button.default'));
      },
      error: () => {
        this._snackBar.open(this.translate.instant('user.avatar.upload_failed'), this.translate.instant('message.button.default'));
      },
    });
  }

  private getContacts(): void {
    this.isContactsChanged = false;
    this.contactsService
      .get({
        user: 'me',
      })
      .subscribe({
        next: (response) => {
          this.contacts = response.results;
          this.isLoading = false;
        },
      });
  }

  public updateProfile(): void {
    if (this.profileForm.invalid) return;

    let options: UserDataInterface = {
      realname: this.profileForm.value.display_name,
      email: this.profileForm.value.email,
    };
    if (this.isUpdatingPassword) {
      options = {
        ...options,
        password: this.profileForm.value.password,
      };
    }

    this.profileForm.disable();
    this.usersService.updateCurrentUser(options).subscribe({
      next: () => {
        this.saveUserSettings().subscribe({
          next: () => window.location.reload(),
          error: () => window.location.reload(),
        });
      },
    });
  }

  private saveUserSettings() {
    const observables: Observable<any>[] = [];

    const selectedStyle = this.profileForm.value.map_style;
    if (!selectedStyle) {
      if (this.mapSettingId) {
        observables.push(this.usersService.deleteUserSetting(String(this.profile.id), this.mapSettingId));
      }
    } else {
      const mapConfig = {
        user_id: this.profile.id,
        id: this.mapSettingId || null,
        config_key: 'map_style',
        config_value: selectedStyle,
      };
      if (this.mapSettingId) {
        observables.push(this.usersService.updateUserSettings(String(this.profile.id), mapConfig, this.mapSettingId));
      } else {
        observables.push(this.usersService.postUserSettings(String(this.profile.id), mapConfig));
      }
    }

    const maxMarkers = this.profileForm.value.max_markers;
    if (!maxMarkers || maxMarkers === '0') {
      if (this.maxMarkersSettingId) {
        observables.push(this.usersService.deleteUserSetting(String(this.profile.id), this.maxMarkersSettingId));
      }
    } else {
      const maxMarkersConfig = {
        user_id: this.profile.id,
        id: this.maxMarkersSettingId || null,
        config_key: 'max_markers',
        config_value: maxMarkers,
      };
      if (this.maxMarkersSettingId) {
        observables.push(this.usersService.updateUserSettings(String(this.profile.id), maxMarkersConfig, this.maxMarkersSettingId));
      } else {
        observables.push(this.usersService.postUserSettings(String(this.profile.id), maxMarkersConfig));
      }
    }

    localStorage.setItem(
      `${generalHelpers.CONST.LOCAL_STORAGE_PREFIX}max_markers`,
      this.profileForm.value.max_markers || '0',
    );

    if (observables.length === 0) return of(null);
    if (observables.length === 1) return observables[0];
    return forkJoin(observables);
  }

  public updateContacts(): void {
    forkJoin(
      this.contacts.map((contact) =>
        this.contactsService.update(contact.id, {
          contact: contact.contact,
          can_notify: contact.can_notify,
        }),
      ),
    ).subscribe({
      complete: () => {
        this.getContacts();
        this.closeModal();
      },
    });
  }

  public updatePassword(isOpen: boolean): void {
    this.isUpdatingPassword = isOpen;

    if (this.isUpdatingPassword) {
      this.updatePasswordFields.nativeElement.scrollIntoView({ behavior: 'smooth' });
      setTimeout(() => {
        this.updatePasswordFields.nativeElement.scrollIntoView({ behavior: 'smooth' });
      }, 350);
    }

    if (this.isUpdatingPassword) {
      this.setFieldsValidators(
        [this.profileForm.controls['password'], this.profileForm.controls['confirmPassword']],
        [Validators.required, Validators.minLength(8), Validators.maxLength(64)],
      );
    } else {
      this.setFieldsValidators(
        [this.profileForm.controls['password'], this.profileForm.controls['confirmPassword']],
        [],
      );
      this.profileForm.patchValue({ password: '', confirmPassword: '' });
    }
  }

  private setFieldsValidators(controls: AbstractControl[], validators: ValidatorFn[]): void {
    controls.map((control) => {
      control.setValidators(validators);
      control.updateValueAndValidity();
    });
  }

  public getTabInformation(): void {
    this.isLoading = true;
    switch (this.selectedTabIndex) {
      case 0:
        this.getProfile();
        break;

      case 1:
        this.getContacts();
        this.getNotifications();
        break;
    }
  }

  public applyChanges(): void {
    switch (this.selectedTabIndex) {
      case 0:
        this.updateProfile();
        break;

      case 1:
        this.updateContacts();
        break;
    }
  }

  public async deleteContact(id: number): Promise<void> {
    const contactToBeDeleted = this.contacts.find((contact) => contact.id == id);
    if (!contactToBeDeleted?.can_notify) {
      this.contactsService.delete(id).subscribe({
        next: () => {
          this.getContacts();
        },
      });
    } else {
      const dialogRef = this.dialog.open(DeleteContactModalComponent, {
        width: '100%',
        maxWidth: 576,
        panelClass: ['modal', 'select-languages-modal'],
        data: {
          contactId: id,
          contactType: contactToBeDeleted.type,
          contacts: this.contacts,
        },
      });

      dialogRef.afterClosed().subscribe((changedContacts: ContactsInterface[]) => {
        if (!changedContacts) return;

        // TODO: This should be a batch update.
        const requests = changedContacts.map((contact) =>
          this.contactsService.update(contact.id, contact),
        );
        requests.push(this.contactsService.delete(id));

        forkJoin(requests).subscribe({
          next: () => {
            this.getContacts();
          },
        });
      });
    }
  }

  public addAccount(): void {
    if (this.addAccountForm.invalid) return;

    this.addAccountForm.disable();
    this.contactsService
      .post({
        active: true,
        can_notify: true,
        contact: this.addAccountForm.value.name,
        type: this.addAccountForm.value.type,
      })
      .subscribe({
        next: () => {
          this.getContacts();
          this.addAccountForm.reset();
          this.addAccountForm.markAsPristine();
          this.addAccountForm.enable();
          this.initAccountForm();
        },
      });
  }

  private closeModal(): void {
    this.matDialogRef.close();
  }

  public accountFormatChanged(event: MatRadioChange): void {
    const value: AccountTypeEnum = event.value;

    const actions = {
      [AccountTypeEnum.Email]: () => [
        Validators.required,
        Validators.pattern(regexHelper.emailValidate()),
      ],
      [AccountTypeEnum.Phone]: () => [
        Validators.required,
        Validators.pattern(regexHelper.phonePattern()),
      ],
    };

    if (actions[value]) {
      const validators = actions[value]();
      this.setFieldsValidators([this.addAccountForm.controls['name']], validators);
    }
  }

  private getNotifications(): void {
    this.notificationsService.get().subscribe({
      next: (response) => {
        this.notifications = response.results;

        this.notifications.map((notification) => {
          this.collectionsService.getById(String(notification.set_id)).subscribe({
            next: (res) => {
              this.applyNotificationName(
                res.result.id,
                res.result.name,
                NotificationTypeEnum.Collection,
              );
            },
            error: () => {
              this.savedsearchesService.getById(String(notification.set_id)).subscribe({
                next: (res) => {
                  this.applyNotificationName(
                    res.result.id!,
                    res.result.name!,
                    NotificationTypeEnum.SavedSearch,
                  );
                },
              });
            },
          });
        });
      },
    });
  }

  private applyNotificationName(
    notificationId: number,
    name: string,
    type: NotificationTypeEnum,
  ): void {
    const notification = this.notifications.find((n) => n.set_id === notificationId);
    if (notification) {
      notification.type = type;
      notification.set_name = name;
    }
  }

  public async deleteNotification(id: string): Promise<void> {
    const confirmed = await this.confirmModalService.open({
      title: this.translate.instant(
        'notification.are_you_sure_you_want_to_remove_this_notification',
      ),
      description: this.translate.instant('notify.default.proceed_warning'),
    });
    if (!confirmed) return;

    this.notificationsService.delete(id).subscribe({
      next: async () => {
        this.getNotifications();

        await this._snackBar.open(
          this.translate.instant('notification.removed'),
          this.translate.instant('message.button.default'),
        );
      },
    });
  }

  public filteredNotifications(type: NotificationTypeEnum): AccountNotificationsInterface[] {
    return this.notifications.filter((notification) => notification.type === type);
  }

  public validateContact(contact: ContactsInterface) {
    this.isContactsChanged = true;
    let error: string | null = null;

    if (contact.type === 'email') {
      const emailPattern = new RegExp(regexHelper.emailValidate());
      if (!emailPattern.test(contact.contact)) {
        error = 'Invalid Email';
      }
    }

    if (contact.type === 'phone') {
      const phonePattern = new RegExp(regexHelper.phonePattern());
      if (!phonePattern.test(contact.contact)) {
        error = 'Invalid Phone';
      }
    }

    this.errorMap[contact.id] = error ? error : null;
  }

  public checkContactsErrors(): boolean {
    return Object.values(this.errorMap).some((error) => error !== null);
  }

  public isButtonDisabled(): boolean {
    if (this.isLoading) {
      return true;
    }

    if (this.selectedTabIndex === 0) {
      return this.profileForm.invalid || this.profileForm.pristine || this.profileForm.disabled;
    }

    if (this.selectedTabIndex === 1) {
      return this.checkContactsErrors();
    }

    return false;
  }
}
